import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { saveAs } from 'file-saver-es';
import { FormValidatorService } from 'src/app/shared/services/form-validator.service';
import { KYCReportStatusService } from '../kyc-report-status.service';
import { KYCUpdateBussines, KYC_GetBussines } from './view-kyc.service';


@Component({
	selector: 'app-view-kyc',
	templateUrl: './view-kyc.component.html',
	styleUrls: ['./view-kyc.component.scss']
})

export class ViewKYCComponent implements OnInit {
	formValidator = new FormValidatorService();
	formGroup!: FormGroup;
	businessData: KYCReportData;
	nextId: number;
	prevId: number;
	statusValues = [
		'Complete',
		'Incomplete',
	];
	changed = false;
	links: ActivationRequestLinks[];
	typeValues = [
		'Individual',
		'Company',
	];

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private getBussines: KYC_GetBussines,
		private updateBussines: KYCUpdateBussines,
		private snackBar: MatSnackBar,
		private httpClient: HttpClient,
		private kycList: KYCReportStatusService,
	) { }

	async ngOnInit() {
		this.createFormValidators();
		this.route.params.subscribe(async params => {
			this.getBussinesData(params.id);
			this.nextId = this.kycList.getNext(params.id);
			this.prevId = this.kycList.getPrev(params.id);
		});
	}

	async submit() {
		try {
			let taxNumber = this.formGroup.controls['TaxNumber'].value;
			if (this.businessData.Type === 'Company') {
				taxNumber = this.unFormatTaxRegistrationNumber(taxNumber);
				this.formGroup.controls['TaxNumber'].patchValue(taxNumber);
			}
			const formData = this.formGroup.value;
			const updated = {
				BusinessId: this.businessData.BusinessId,
				BusinessName: this.businessData.BusinessName,
				Type: this.businessData.Type,
				...formData
			}
			this.updateBussines.setParams(updated);
			taxNumber = this.formatTaxRegistrationNumber(taxNumber);
			this.formGroup.controls['TaxNumber'].patchValue(taxNumber);
			await this.updateBussines.make();
			this.snackBar.open('Updated!', null, { duration: 2000 });
			this.changed = false;
			this.getBussinesData(this.businessData.BusinessId);
		} catch (error) {
			console.log(error);
		}
	}

	nextOrPrevious(type: string) {
		if (this.changed) {
			this.showWarnning();
			return;
		}

		if (type === 'next') {
			this.router.navigate([`admin/reports/kyc-report/${this.nextId}`]);
		} else if (type === 'prev') {
			this.router.navigate([`admin/reports/kyc-report/${this.prevId}`]);
		}
	}

	download(name: string) {
		const url = this.getLink(name);
		const splitUrl = url.split('.')
		const extension = splitUrl[splitUrl.length - 1]

		return this.httpClient.get(url,
			{ responseType: 'blob' }).subscribe((data) => {
				const blob = new Blob([data]);
				saveAs(blob, `${name}.${extension}`);
			});
	}

	showWarnning() {
		this.changed = true;
		this.snackBar.open('Please Submit your changes!', null, { duration: 3000 });
	}

	formatTaxNumber() {
		this.showWarnning();
		let taxNumber = this.formGroup.controls['TaxNumber'].value;
		taxNumber = this.unFormatTaxRegistrationNumber(taxNumber);
		taxNumber = this.formatTaxRegistrationNumber(taxNumber);
		this.formGroup.controls['TaxNumber'].patchValue(taxNumber);
	}

	getFormControl(name: string): FormControl {
		return this.formGroup.get(name) as FormControl;
	}

	getLink(name: string): string {
		return this.links.find(l => l.name === name)?.value;
	}

	private createFormValidators() {
		this.formGroup = this.formValidator.createForm([
			{
				fieldName: 'BusinessId',
				defaultValue: null,
			},
			{
				fieldName: 'BusinessName',
				defaultValue: null,
			},
			{
				fieldName: 'Type',
				defaultValue: null,
			},
			{
				fieldName: 'Status',
				defaultValue: null,
			},
			{
				fieldName: 'TaxNumber',
				defaultValue: null,
			},
			{
				fieldName: 'LegalName',
				defaultValue: null,
			},
			{
				fieldName: 'NationalId',
				defaultValue: null,
			},
			{
				fieldName: 'NationalName',
				defaultValue: null,
			}
		]);
	}
	private setFormValues() {
		this.formGroup.controls['BusinessId']
			.setValue(this.businessData.BusinessId);
		this.formGroup.controls['BusinessName']
			.setValue(this.businessData.BusinessName);
		this.formGroup.controls['Type'].setValue(this.businessData.Type);
		this.formGroup.controls['Status'].setValue(this.businessData.Status);
		this.formGroup.controls['LegalName']
			.setValue(this.businessData.LegalName);
		this.formGroup.controls['NationalId']
			.setValue(this.businessData.NationalId);
		this.formGroup.controls['NationalName']
			.setValue(this.businessData.NationalName);
		const taxNumber =
			this.formatTaxRegistrationNumber(this.businessData.TaxNumber);
		this.formGroup.controls['TaxNumber'].setValue(taxNumber);
	}

	private disableValues() {
		this.formGroup.controls['BusinessId'].disable({ onlySelf: true });
		this.formGroup.controls['BusinessName'].disable({ onlySelf: true });
	}

	private formatTaxRegistrationNumber(num: string): string {
		return num?.replace(/\B(?=(\d{3})+(?!\d))/g, "-");
	}

	private unFormatTaxRegistrationNumber(num: string): string {
		return num?.replace(/-/g, '');
	}
	private async getBussinesData(businessId: number) {
		this.getBussines.setParams({ businessId });
		const res = await this.getBussines.make();
		this.businessData = res.data.report;
		this.links = res.data.links;
		this.setFormValues();
		this.disableValues();
	}
}

type KYCReportData = {
	BusinessId: number;
	BusinessName: string;
	Type: string;
	Status: string,
	TaxNumber: string;
	LegalName: string;
	NationalId: string;
	NationalName: string;
};

type ActivationRequestLinks = {
	name: string;
	value: string;
};
